import { GroupData } from '@bringg/types';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

import useOrderExplorerStore from '../../order-explorer/stores/use-order-explorer-store';
import { orderExplorerGroupLoader } from 'bringg-web/features/monitoring/order-explorer/data/order-explorer-group-loader';

export const useGroupSelection = (group: GroupData) => {
	const {
		orderExplorerStore,
		selectedTasksStore: { isTaskSelected, selectMany }
	} = useOrderExplorerStore();

	const allItemsSelected = (groupId: string): boolean => {
		const groupTasks = orderExplorerStore.currentTableDefinition.itemsByGroup[groupId];
		return groupTasks?.every(task => isTaskSelected(task.id));
	};

	const getSelectionState = (): [boolean, boolean] => {
		let selectedItems = 0;
		let totalItemsCount = 0;
		if (group.groups) {
			selectedItems = group.groups.filter(innerGroup => allItemsSelected(innerGroup.id)).length;
			totalItemsCount = group.groups.length;
		} else {
			const groupTasks = orderExplorerStore.currentTableDefinition.itemsByGroup[group.id];
			selectedItems = (groupTasks || []).filter(task => isTaskSelected(task.id)).length;
			totalItemsCount = groupTasks?.length || 0;
		}

		const isAllSelected = selectedItems > 0 && selectedItems === totalItemsCount;
		const isPartiallySelected = selectedItems > 0 && selectedItems < totalItemsCount;

		return [isAllSelected, isPartiallySelected];
	};

	const [isSelected, isPartiallySelected] = getSelectionState();

	const unselectGroup = () => {
		let ids: number[];
		if (group.groups) {
			ids = group.groups.reduce((allIds, innerGroup) => {
				const innerGroupIds = orderExplorerStore.currentTableDefinition.itemsByGroup[innerGroup.id].map(
					item => item.id
				);
				allIds.push(...innerGroupIds);
				return allIds;
			}, []);
		} else {
			ids = orderExplorerStore.currentTableDefinition.itemsByGroup[group.id].map(item => item.id);
		}

		const tasks = getRootEnv().dashboardSdk.sdk.v2.tasks.getMany(ids);
		selectMany(tasks, false);
	};

	const selectGroup = async (shouldSelect: boolean, useFilters = true) => {
		if (!shouldSelect) {
			unselectGroup();
			return;
		}

		const items = await orderExplorerGroupLoader.load(group, useFilters);
		const ids = items.map(({ id }) => id);
		const tasks = getRootEnv().dashboardSdk.sdk.v2.tasks.getMany(ids);

		selectMany(tasks, shouldSelect);
	};

	return {
		isSelected,
		isPartiallySelected,
		selectGroup
	};
};
